import React, { useEffect, useRef } from "react";

const CircleBackgroundScale = ({ children }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    let frame;
    let width = 0;
    let height = 0;
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();

      width = rect.width;
      height = rect.height;

      canvas.width = width * dpr;
      canvas.height = height * dpr;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();

    const resizeObserver = new ResizeObserver(() => {
      resize();
    });

    resizeObserver.observe(canvas);

    const points = 150;

    const circles = [
      {
        offset: 0,
        radius: 150,
        speed: 1,
        xOffset: -80,
        yOffset: -80,
      },
      {
        offset: 2,
        radius: 150,
        speed: 0.8,
        xOffset: 0,
        yOffset: 0,
      },
      {
        offset: 6,
        radius: 150,
        speed: 0.6,
        xOffset: 80,
        yOffset: 80,
      },
    ];

    const draw = (t) => {
      const scale = Math.min(width, height) / 700;
      const time = t * 0.001;

      const getCssVar = (name) =>
        getComputedStyle(document.documentElement)
          .getPropertyValue(name)
          .trim();

      // Background
      ctx.fillStyle = getCssVar("--animatedNeonBg");
      ctx.fillRect(0, 0, width, height);

      // Grid
      ctx.save();
      ctx.strokeStyle = getCssVar("--animatedNeonBg");
      ctx.lineWidth = 1;

      const gridSize = 10 * scale;

      for (let x = 0; x < width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }

      for (let y = 0; y < height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }

      ctx.restore();

      const centerX = width / 2;
      const centerY = height / 2;

      circles.forEach((c) => {
        ctx.save();

        // Stair-step positioning
        ctx.translate(
          centerX + c.xOffset * scale,
          centerY + c.yOffset * scale
        );

        const gradient = ctx.createLinearGradient(
          Math.cos(time + c.offset) * 200,
          Math.sin(time + c.offset) * 200,
          Math.cos(time + 2 + c.offset) * 200,
          Math.sin(time + 2 + c.offset) * 200
        );

        gradient.addColorStop(0, "#00e5ff44");
        gradient.addColorStop(0.5, "#b300ff48");
        gradient.addColorStop(1, "#00ffa249");

        ctx.beginPath();

        for (let i = 0; i <= points; i++) {
          const angle = (i / points) * Math.PI * 2;

          const noise =
            (Math.sin(angle * 3 + time * 2 * c.speed) * 10 +
              Math.cos(angle * 5 - time * 1.5 * c.speed) * 8 +
              Math.sin(angle * 2 - time * 3 * c.speed) * 6) *
            scale;

          const radius = c.radius * scale + noise;

          const px =
            Math.cos(angle + time * 0.6 * c.speed) * radius;
          const py =
            Math.sin(angle + time * 0.6 * c.speed) * radius;

          if (i === 0) {
            ctx.moveTo(px, py);
          } else {
            ctx.lineTo(px, py);
          }
        }

        ctx.closePath();

        ctx.shadowBlur = 20;
        ctx.shadowColor = "#00e5ff";

        ctx.strokeStyle = gradient;
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.restore();
      });

      frame = requestAnimationFrame(draw);
    };

    frame = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div className="matrix_background fade-in">
      <canvas ref={canvasRef} />
      <div className="mtx_children">{children}</div>
    </div>
  );
};

export default CircleBackgroundScale;